import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listTaskStatuses,
  createTaskStatus,
  updateTaskStatus,
  reorderTaskStatuses,
  deleteTaskStatus,
  type CreateTaskStatusInput,
  type TaskStatusCategory,
  type TaskStatusDef,
  type UpdateTaskStatusInput,
} from '../lib/taskStatusesApi';

// CLE-DASH — configurable task statuses. The shared `['task-statuses']` key is
// read by the board/list/filters and the Settings tab; every mutation
// invalidates it plus the task queries (the task serialization embeds the
// status name/color, so a rename must refetch tasks too).

export const TASK_STATUSES_KEY = ['task-statuses'] as const;

// Grouping order for the Linear-style list: active work first (STARTED, most
// advanced first), then queued (UNSTARTED, BACKLOG), then terminal states.
const CATEGORY_GROUP_RANK: Record<TaskStatusCategory, number> = {
  STARTED: 0,
  UNSTARTED: 1,
  BACKLOG: 2,
  COMPLETED: 3,
  CANCELLED: 4,
};

export interface TaskStatusesView {
  statuses: TaskStatusDef[];
  byId: Record<string, TaskStatusDef>;
  bySlug: Record<string, TaskStatusDef>;
  /** Kanban columns: workflow order, without CANCELLED. */
  boardColumns: TaskStatusDef[];
  /** List group order: STARTED (desc) → UNSTARTED → BACKLOG → COMPLETED → CANCELLED. */
  listGroupOrder: TaskStatusDef[];
  defaultStatus: TaskStatusDef | undefined;
}

export function buildTaskStatusesView(statuses: TaskStatusDef[]): TaskStatusesView {
  const sorted = [...statuses].sort((a, b) => a.order - b.order);
  const byId: Record<string, TaskStatusDef> = {};
  const bySlug: Record<string, TaskStatusDef> = {};
  for (const s of sorted) {
    byId[s.id] = s;
    bySlug[s.slug] = s;
  }
  const listGroupOrder = [...sorted].sort((a, b) => {
    const ra = CATEGORY_GROUP_RANK[a.category];
    const rb = CATEGORY_GROUP_RANK[b.category];
    if (ra !== rb) return ra - rb;
    // Within STARTED the most advanced stage goes first (In Review before
    // In Progress), matching the old hardcoded group order.
    return a.category === 'STARTED' ? b.order - a.order : a.order - b.order;
  });
  return {
    statuses: sorted,
    byId,
    bySlug,
    boardColumns: sorted.filter((s) => s.category !== 'CANCELLED'),
    listGroupOrder,
    defaultStatus: sorted.find((s) => s.isDefault) ?? sorted[0],
  };
}

export function useTaskStatuses() {
  const query = useQuery({
    queryKey: TASK_STATUSES_KEY,
    queryFn: listTaskStatuses,
    staleTime: 5 * 60 * 1000,
  });

  const view = useMemo(() => buildTaskStatusesView(query.data ?? []), [query.data]);

  return { ...query, ...view };
}

function useInvalidateStatuses() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: TASK_STATUSES_KEY });
    void qc.invalidateQueries({ queryKey: ['tasks'] });
    void qc.invalidateQueries({ queryKey: ['task-estimate-totals'] });
  };
}

export function useCreateTaskStatus() {
  const invalidate = useInvalidateStatuses();
  return useMutation({
    mutationFn: (input: CreateTaskStatusInput) => createTaskStatus(input),
    onSuccess: invalidate,
  });
}

export function useUpdateTaskStatusDef() {
  const invalidate = useInvalidateStatuses();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTaskStatusInput }) =>
      updateTaskStatus(id, input),
    onSuccess: invalidate,
  });
}

export function useReorderTaskStatuses() {
  const invalidate = useInvalidateStatuses();
  return useMutation({
    mutationFn: (statusIds: string[]) => reorderTaskStatuses(statusIds),
    onSuccess: invalidate,
  });
}

export function useDeleteTaskStatus() {
  const invalidate = useInvalidateStatuses();
  return useMutation({
    mutationFn: ({ id, reassignTo }: { id: string; reassignTo?: string }) =>
      deleteTaskStatus(id, reassignTo),
    onSuccess: invalidate,
  });
}
